import { Box, Button, Card, Container, Grid, IconButton, List, ListItem, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Table, TableBody, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { enqueueSnackbar } from "notistack";
import axios, { endpoints } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { EldDevice, EldVehicle, Device } from "src/models";
import useModelData, { replaceById } from "src/hooks/use-model-data";
import Iconify from "src/components/iconify/iconify";
import { DisplayPageData } from "src/components/display-page-data";
import PageHeader from "src/components/page-header";
import NoneFound from "src/components/none-found";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import { TruxTableCell } from "src/components/trux-table-cell";
import { ColoredIcon } from "src/components/colored-icon";
import { useSearch } from "src/hooks/use-search";
import { SearchCard } from "src/components/search-card";
import Label from "src/components/label";
import SimpleDialog from "src/components/simple-dialog";
import TruxFormField from "src/components/inputs/trux-form-field";
import TruxAutocompleteField from "src/components/inputs/trux-autocomplete-field";
import TruxSimpleCheckboxField from "src/components/inputs/trux-simple-checkbox-field";

const processExternalIdentifier = (externalIdentifier?: string): string => {
  if (!externalIdentifier) return '';
    const parts = externalIdentifier.split("|");
    if (parts.length < 2) return '';

    const secondPart = parts[1];
    const spacesRemoved = secondPart.split(" ").join("");

    return spacesRemoved.startsWith("0") ? spacesRemoved.substring(1) : spacesRemoved;
};

type EldDeviceWithEldVehicle = EldDevice & {
  eldVehicle?: EldVehicle,
  device?: Device,
  externalIdentifier?: string,
}

type EldDevicesProps = {
  eldDevices?: EldDeviceWithEldVehicle[],
  onEldDeviceSelected: (a: EldDeviceWithEldVehicle) => void
}

const EldDevices = memo(({ eldDevices, onEldDeviceSelected }: EldDevicesProps) => {
  const { sortedList, sortData } = useSort(eldDevices);

  const {
    searchText,
    setSearchText,
    filteredList,
  } = useSearch(sortedList, ["deviceType", "identifier", "externalIdentifier"]);

  if (!sortedList) {
    return null;
  }

  return <div>
    <SearchCard searchText={searchText} setSearchText={setSearchText} searchingFor="EldDevices" />

    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table>
          <TableHead>
            <TableRow>
              <SortableHeader sortKey="deviceType" sortData={sortData} >
                Device Type
              </SortableHeader>

              <SortableHeader sortKey="identifier" sortData={sortData} >
                Device Identifier
              </SortableHeader>

              <SortableHeader sortKey="externalIdentifier" sortData={sortData} >
                External Identifier
              </SortableHeader>

              <TruxTableCell>
                Assigned To
              </TruxTableCell>

              <SortableHeader sortKey="lastVinReported" sortData={sortData} >
                Last VIN Reported
              </SortableHeader>

              <SortableHeader sortKey="active" sortData={sortData} >
                Active
              </SortableHeader>

              <SortableHeader sortKey="active" sortData={sortData} >
                Action
              </SortableHeader>
            </TableRow>
          </TableHead>


          <TableBody>
            {filteredList.map((eldDevice, index) =>
            (<TableRow
              key={index}
              hover
              onClick={() => onEldDeviceSelected(eldDevice)}
              sx={{ cursor: "pointer" }}
            >
              <TruxTableCell>
                {eldDevice.deviceType}
              </TruxTableCell>

              <TruxTableCell>
                {eldDevice.identifier}
              </TruxTableCell>

              <TruxTableCell>
                {eldDevice.externalIdentifier}
              </TruxTableCell>

              <TruxTableCell>
                #{eldDevice.eldVehicle?.vehicleUnitNumber} - {eldDevice.eldVehicle?.vin}
              </TruxTableCell>

              <TruxTableCell>
                {eldDevice.lastVinReported}
              </TruxTableCell>

              <TruxTableCell>
                {eldDevice.active &&
                  <Label variant="filled" color="success" sx={{ cursor: "pointer" }}>Active</Label>}

                {!eldDevice.active &&
                  <Label variant="filled" color="error" sx={{ cursor: "pointer" }}>Inactive</Label>}
              </TruxTableCell>

              <TruxTableCell>
                <EldDeviceActions eldDevice={eldDevice} />
              </TruxTableCell>
            </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});

function EldDeviceActions({eldDevice}: {eldDevice: EldDeviceWithEldVehicle}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  return <>
    <IconButton onClick={(event) => {
      event.stopPropagation();
      event.preventDefault();
      setAnchorEl(event.currentTarget);
    }}>
      <Iconify icon="solar:menu-dots-bold" rotate={45} />
    </IconButton>
    {open && <Menu
      id="basic-menu"
      anchorEl={anchorEl}
      open={open}
      onClose={() => setAnchorEl(null)}
      MenuListProps={{
        'aria-labelledby': 'basic-button',
      }}
    >
      <MenuItem onClick={(event) => {
        event.stopPropagation();
        event.preventDefault();
        setAnchorEl(null)
        axios.post(endpoints.eldDevices.alarm, {eldDeviceId: eldDevice.id, action: "start"}).then(() => enqueueSnackbar("Successfully sent start alarm command!")).catch(() => enqueueSnackbar("Failed to send start alarm command!", {variant: "error"}));
      }}>Start Alarm</MenuItem>
      <MenuItem onClick={(event) => {
        event.stopPropagation();
        event.preventDefault();
        setAnchorEl(null)
        axios.post(endpoints.eldDevices.alarm, {eldDeviceId: eldDevice.id, action: "stop"}).then(() => enqueueSnackbar("Successfully sent stop alarm command!")).catch(() => enqueueSnackbar("Failed to send stop alarm command!", {variant: "error"}));
      }}>Stop Alarm</MenuItem>
    </Menu>}
  </>;
}

type PageModel = {
  eldDevices?: EldDeviceWithEldVehicle[],
  eldVehicles?: EldVehicle[],
  devices?: Device[],
}

export default function Page() {
  const settings = useSettingsContext();
  const dataLoader = useCallback(() => ({
    eldDevices: axios.get(endpoints.eldDevices.findAll).then(response => response.data),
    eldVehicles: axios.get(endpoints.eldVehicles.findAll).then(response => response.data),
    devices: axios.get(endpoints.devices.findAll).then(response => response.data),
  }), []);
  const {
    data,
    updateData,
    setData,
    error,
    loading,
    setShouldRefresh
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      console.log(error);
      alert(error);
    }
  }, [loading, error]);

  const devicesWithVehicle = useMemo(() => {
    if (!data?.eldDevices || !data?.eldVehicles || !data?.devices) return [];
    return data.eldDevices.map(d => {
      const eldVehicle = data.eldVehicles!.find(v => v.mac === `WQ-${d.identifier}`);
      const device = eldVehicle?.deviceId
        ? data.devices!.find(dev => dev.id === eldVehicle.deviceId)
        : undefined;

      return {
        ...d,
        eldVehicle,
        device,
        externalIdentifier: processExternalIdentifier(device?.externalIdentifier)
      };
    });
  }, [data?.eldDevices, data?.eldVehicles, data?.devices]);

  const [isOpen, setIsOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);

  const [selectedEldDevice, setSelectedEldDevice] = useState<EldDeviceWithEldVehicle>();

  const selectedVehicle = data?.eldVehicles?.find(eldVehicle => eldVehicle.id === selectedEldDevice?.eldVehicle?.id);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {data && data?.eldDevices?.length !== 0 &&
          <PageHeader title="ELD Devices" />
        }

        {devicesWithVehicle.length
          ? <EldDevices
              eldDevices={devicesWithVehicle}
              onEldDeviceSelected={(eldDevice: EldDeviceWithEldVehicle) => {
                setSelectedEldDevice({...eldDevice});
                setIsOpen(true);
              }}
            />
          : <NoEldDevicesFound />
        }

        {isOpen && <SimpleDialog
            title="Assign ELD Device"
            open={isOpen}
            onCancel={() => setIsOpen(false)}
            onSubmit={() => {
              setDialogLoading(true);

              if(!selectedEldDevice) {
                return;
              }

              axios.post(endpoints.eldDevices.assign, {
                deviceId: selectedEldDevice.id,
                deviceType: selectedEldDevice.deviceType,
                active: selectedEldDevice.active,
                eldVehicleId: selectedEldDevice.eldVehicle?.id,
              }).then((response) => {
                const eldDevice: EldDevice = response.data;
                updateData('eldDevices')(replaceById(data!.eldDevices!, eldDevice))
                enqueueSnackbar("Successfully updated the ELD Device!");
                setShouldRefresh(true);
                setIsOpen(false);
              }).finally(() => {
                setDialogLoading(false);
              })
            }}
            submitDisabled={dialogLoading}
            submitButton="Assign ELD Device"
            submitButtonIcon={<Iconify icon="basil:save-solid" />}
            submitButtonColor="info">

            <Box display="flex" flexDirection="column" gap="15px">
              <TruxFormField label="Truck">
                <TruxAutocompleteField
                  options={data?.eldVehicles?.map(eldVehicle => ({
                    label: `${eldVehicle.vehicleUnitNumber} - ${eldVehicle.vin}`,
                    id: eldVehicle.id
                  })) || []}
                  onClear={() => {
                    if (selectedEldDevice) {
                      setSelectedEldDevice(undefined);
                    }
                  }}
                  onChange={(value) => {
                    console.log(value)
                    if (selectedEldDevice) {
                      if (value == null) {
                        setSelectedEldDevice((current) => (!current ? current : {...current}));
                      } else {
                        data?.eldVehicles?.forEach(vehicle => {
                          if (value.id === vehicle.id) {
                            // setSelectedVehicle(eldVehicle);
                            setSelectedEldDevice(prev => {
                            if (!prev) return prev;
                            return {
                              ...prev,
                              eldVehicle: vehicle,
                              };
                            });
                          }
                        })
                      }
                    }
                  }}
                  selected={{
                    id: selectedVehicle ? selectedVehicle.id : 0,
                    label: selectedVehicle ? `${selectedVehicle.vehicleUnitNumber} - ${selectedVehicle.vin}` : `Not Assigned`,
                  }}
                />
              </TruxFormField>
              <TruxFormField label="Active">
                <TruxSimpleCheckboxField
                  size="medium"
                  value={selectedEldDevice?.active || false}
                  onChange={() => {
                    setSelectedEldDevice(prev => {
                      if (!prev) return prev; // Prevent error if prev is undefined
                      return {
                        ...prev,
                        active: !prev.active,
                      };
                    });
                  }}
                />
              </TruxFormField>
            </Box>
          </SimpleDialog>
        }
      </DisplayPageData>
    </Container>
  );
}

export function NoEldDevicesFound() {
  return <NoneFound title="GET REALTIME LOCATION AND DIAGNOSTICS">
    <Box display="flex" flexDirection="column" width="100%">
      <Box sx={{ display: "flex", flexDirection: { xs: 'column', md: 'row' }, gap: 4, alignItems: 'flex-start' }}>
        {/* Features Section */}
        <Box sx={{ flex: 2 }}>
          <Grid container spacing={3}>
            {/* ADAS Features */}
            <Grid item xs={12} sm={6}>
              <Paper elevation={2} sx={{ p: 3, height: '100%', bgcolor: 'primary.50' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                  GEOMETRIS WHEREQUBE
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <ColoredIcon color="success">
                        <Iconify icon="eva:checkmark-circle-2-fill" width={24} />
                      </ColoredIcon>
                    </ListItemIcon>
                    <ListItemText
                      primary="Always Connected"
                      secondary="Internal internet connection"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <ColoredIcon color="success">
                        <Iconify icon="eva:checkmark-circle-2-fill" width={24} />
                      </ColoredIcon>
                    </ListItemIcon>
                    <ListItemText
                      primary="Realtime Location"
                      secondary="Helps optimize fleet management and dispatch"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <ColoredIcon color="success">
                        <Iconify icon="eva:checkmark-circle-2-fill" width={24} />
                      </ColoredIcon>
                    </ListItemIcon>
                    <ListItemText
                      primary="Diagnostics"
                      secondary="High quality diagnostics help minimize maintenance costs"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>

            {/* DMS Features */}
            <Grid item xs={12} sm={6}>
              <Paper elevation={2} sx={{ p: 3, height: '100%', bgcolor: 'secondary.50' }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'secondary.main', fontWeight: 'bold' }}>
                  INTEGRATION PROCESS
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <ColoredIcon color="success">
                        <Iconify icon="emojione-monotone:digit-one" width={24} />
                      </ColoredIcon>
                    </ListItemIcon>
                    <ListItemText
                      primary="Reach out"
                      secondary="Contact our Sales team and learn about Geometris"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <ColoredIcon color="success">
                        <Iconify icon="emojione-monotone:digit-two" width={24} />
                      </ColoredIcon>
                    </ListItemIcon>
                    <ListItemText
                      primary="Receive New Devices"
                      secondary="We will ship devices configured for our ELD"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <ColoredIcon color="success">
                        <Iconify icon="emojione-monotone:digit-three" width={24} />
                      </ColoredIcon>
                    </ListItemIcon>
                    <ListItemText
                      primary="Device Exchange"
                      secondary="If you send us your existing Gometris device new devices are free"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
          <Grid container spacing={3} pt={1}>
            <Grid item xs={12}>
              <Paper elevation={2} sx={{ p: 3, height: '100%', bgcolor: 'secondary.50' }}>
                * Your existing devices can be used but they will not report ELD data and location in realtime. To get realtime connection to your Vehicle for <b>free</b> please reach out to your sales contact or directly at sales@countingtrucks.com and we will exchange your devices with new devices.
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Image Section */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box
            component="img"
            src="https://accountingimageuploads.s3.us-west-2.amazonaws.com/whereqube.png"
            alt="WhereQube by Geometris"
            sx={{
              width: '100%',
              maxWidth: 360,
              height: 'auto',
            }}
          />
          <Box sx={{ p: 2, width: '100%', textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              WhereQube by Geometris
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              A vehicle Gateway that can report the GPS location and the vehicle data remotely even when not connected to a tablet inside of the Truck.
            </Typography>
            <Button
              variant="contained"
              color="success"
              href="mailto:sales@countingtrucks.com"
              fullWidth
            >
              Contact Sales
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  </NoneFound>;
}
